import React, { useState } from 'react';
import { FaArrowLeft, FaArrowRight, FaTimes } from 'react-icons/fa';
import { MdArrowBack } from "react-icons/md";
import ExcelIcon from '../../assets/images/vscode-icons_file-type-excel.svg';
import * as XLSX from 'xlsx';
import Skeleton from '../Loader/Skeleton';
import { CiSearch } from "react-icons/ci";
import NewAppointment from '../Appointment/NewAppointment';
import { toast } from 'react-toastify';
import * as apicall from '../../components/Apicall';
import ConfirmationModal from '../Appointment/ConfirmationModal';
import DeleteIcon from "../../assets/images/fluent-color_warning-20.svg";

// Add styles for hover effect
const tableStyles = {
  hoverRow: {
    transition: 'background-color 0.2s ease',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    }
  }
};

const TableSkeletonRow = ({ columns }) => {
  return (
    <tr>
      {Array(columns).fill(0).map((_, index) => (
        <td key={index} style={{ padding: '12px 16px', border: '1px solid #E3EBF8' }}>
          <Skeleton 
            width={index === 0 ? '60%' : '80%'} 
            height="16px" 
            marginBottom="0"
          />
        </td>
      ))}
    </tr>
  );
};

const Table = ({
  title = "Appointments",
  headers = [],
  data = [],
  loading = false,
  searchQuery = "",
  onSearchChange,
  onBackClick,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  onExport,
  showSearch = true,
  showFilters = true,
  showExport = false,
  showPagination = true,
  customFilters,
  getStatusBadgeClass,
  searchPlaceholder = "Search patient, doctor...",
  className = "",
  style = {},
  onRowClick,
  doctors = [],
  timeSlots = [],
  holidayDates = [],
  callApi = null,
  onClose: parentOnClose = () => {}, // Add default onClose prop
}) => {
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentMode, setAppointmentMode] = useState('view');
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Function to handle closing the component
  const onClose = () => {
    setSelectedAppointment(null);
    setAppointmentMode('view');
    setSelectedDate(null);
    setAvailableTimeSlots([]);
    parentOnClose();
  };

  // Add function to fetch doctor availability
  const fetchDoctorAvailability = async (doctorId, date) => {
    if (!doctorId || !date) return;

    try {
      const response = await apicall.get(`get_available_slots/${doctorId}/${date}`);
      if (response && response.data) {
        setAvailableTimeSlots(response.data.available_slots || []);
      } else {
        setAvailableTimeSlots([]);
      }
    } catch (error) {
      toast.error('Failed to fetch available slots');
      setAvailableTimeSlots([]);
    }
  };

  // Update handleRowClick to fetch availability
  const handleRowClick = (row) => {
  
    // Get booking ID from the correct index (index 9)
    const bookingId = row[9]?.toString();
   
    if (!bookingId) {
      console.warn('No booking ID found in row data');
    }

    const appointment = {
      patientName: row[1],
      doctorName: row[2],
      appointmentTime: row[3], // Already in 12-hour format
      appointmentDate: row[4],
      age: row[6] || '',
      gender: row[7],
      mobileNumber: row[8]?.toString() || '',
      insuranceProvider: row[13] || '',
      insuranceNumber: row[14] || '',
      bookingId: bookingId,
      details: {
        patient_name: row[1],
        doctor_name: row[2],
        booking_time: row[3], 
        booking_date: row[4],
        status: row[10],
        patient_age: row[6] || '',
        patient_gender: row[7] === 'Male' ? 1 : 2,
        patient_phone: row[8]?.toString() || '',
        id: bookingId,
        booking_id: bookingId,
        user_id: row[11]?.toString() || '',
        doctor_id: row[12]?.toString() || '',
        insurance_provider: row[13] || '',
        insurance_number: row[14] || ''
      }
    };

    setSelectedAppointment(appointment);
    setAppointmentMode('view');
    setSelectedDate(appointment.details.booking_date);
    
    // Fetch availability when an appointment is selected
    if (appointment.details.doctor_id && appointment.details.booking_date) {
      fetchDoctorAvailability(appointment.details.doctor_id, appointment.details.booking_date);
    }
    
    if (onRowClick) {
      onRowClick(appointment);
    }
  };

  // Update handleCloseAppointment to handle mode changes
  const handleCloseAppointment = (nextMode) => {
    if (nextMode === 'edit') {
      setAppointmentMode('edit');
      // Fetch availability again when switching to edit mode
      if (selectedAppointment?.details?.doctor_id && selectedDate) {
        fetchDoctorAvailability(selectedAppointment.details.doctor_id, selectedDate);
      }
    } else {
      setSelectedAppointment(null);
      setAppointmentMode('view');
      setSelectedDate(null);
      setAvailableTimeSlots([]);
    }
  };

  // Filter data based on search query
  const filteredData = data.filter(row => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    // Only search in visible columns (0-5)
    return row.slice(0, 6).some(cell => 
      cell && cell.toString().toLowerCase().includes(searchLower)
    );
  });

  // Map the filtered data to include the new sequential numbers
  const processedData = filteredData.map((row, index) => {
    const newRow = [...row];
    newRow[0] = index + 1; // Assuming first column is S.No
    return newRow;
  });

  const totalRows = processedData.length;
  const totalPages = Math.ceil(totalRows / pageSize);
  const paginatedData = processedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Function to get page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const totalPagesToShow = 5; // Show max 5 page numbers
    const halfWay = Math.floor(totalPagesToShow / 2);

    if (totalPages <= totalPagesToShow) {
      // If total pages are less than or equal to totalPagesToShow, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      let startPage = Math.max(2, currentPage - halfWay);
      let endPage = Math.min(totalPages - 1, currentPage + halfWay);

      // Adjust start and end if we're near the beginning or end
      if (currentPage <= halfWay + 1) {
        endPage = totalPagesToShow - 1;
      } else if (currentPage >= totalPages - halfWay) {
        startPage = totalPages - totalPagesToShow + 2;
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pageNumbers.push('...');
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
   
    // Get booking ID from the correct location
    const bookingId = selectedAppointment?.bookingId;

    if (!bookingId) {
      console.error('No booking ID found in:', selectedAppointment); // Debug
      toast.error('Booking ID is missing');
      return;
    }

    const payload = {
      booking_id: bookingId,
      appointment_date: selectedAppointment.appointmentDate,
      time_slots: [selectedAppointment.appointmentTime],
      status: 14
    };

 

    try {
    
      const response = await apicall.post('update-booking', payload);
     
      if (response.status === 200) {
        toast.success('Appointment Deleted successfully');
        setShowDeleteConfirm(false);
        if (callApi) callApi(); // Refresh the table data after deletion
        onClose();
      } else {
        console.error('Delete failed:', response); // Debug
        toast.error(response.message || 'Failed to delete appointment');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error?.message || 'Failed to delete appointment');
    }
  };

  // Add helper function for time format conversion
  const convertTo24HourFormat = (slots) => {
    return slots.map(slot => {
      if (!slot) return null;
      const strSlot = String(slot).trim();
      const [time, modifier] = strSlot.split(' ');
      if (!time || !modifier) return null;

      let [hours, minutes] = time.split(':');
      hours = parseInt(hours, 10);
      if (modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
      if (modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;

      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }).filter(Boolean);
  };

  return (
    <div className={`min-vh-100 ${className}`} style={{ backgroundColor: '#F2F5FA', ...style }}>
      <div className="px-4 py-3">
        {onBackClick && (
          <div className="d-flex align-items-center mb-3">
            <button
              className="btn btn-link text-dark p-0"
              onClick={onBackClick}
              style={{ fontSize: '14px', textDecoration: 'none' }}
            >
              <MdArrowBack size={20} /> BACK
            </button>
          </div>
        )}

        <h5 className="mb-4" style={{ fontSize: '18px', fontWeight: '600' }}>{title}</h5>

        <div className="d-flex justify-content-between align-items-center mb-4">
          {showSearch && (
            <div className="position-relative">
              <input
                type="text"
                className="form-control"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => onSearchChange?.(e.target.value)}
                style={{
                  height: '40px',
                  width: '300px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  paddingLeft: '36px',
                  paddingRight: searchQuery ? '36px' : '12px',
                  border: '1px solid #E3EBF8'
                }}
              />
              <CiSearch
                className="position-absolute text-muted"
                style={{ left: '12px', top: '12px', fontSize: '14px' }}
              />
            </div>
          )}

          <div className="d-flex align-items-center gap-3">
            {showFilters && customFilters}

            {showExport && (
              <button
                className="btn d-flex align-items-center gap-2 px-3"
                style={{
                  height: '40px',
                  fontSize: '14px',
                  borderRadius: '6px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E3EBF8',
                  color: '#000000',
                }}
              >
                <img src={ExcelIcon} alt="Excel Icon" style={{ height: '25px', width: '25px' }} />
                Export
              </button>
            )}
          </div>
        </div>

        <div className="table-responsive" style={{ 
          overflowX: 'auto', 
          WebkitOverflowScrolling: 'touch',
          margin: '0 -16px',
          padding: '0 16px'
        }}>
          <table className="table table-bordered" style={{ marginBottom: 0 }}>
            <thead>
              <tr style={{ backgroundColor: '#E2E9F4' }}>
                {headers.map((header, index) => (
                  <th key={index}
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#000',
                      padding: '12px 16px',
                      border: '1px solid #E3EBF8',
                      whiteSpace: 'nowrap',
                      backgroundColor: '#E3EBF8'
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(pageSize).fill(0).map((_, index) => (
                  <TableSkeletonRow key={index} columns={headers.length} />
                ))
              ) : paginatedData.length > 0 ? (
                paginatedData.map((row, rowIndex) => (
                  <tr 
                    key={rowIndex}
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: 'pointer' }}
                    className="hover-row"
                  >
                    {/* Only display the first 6 columns (0-5) */}
                    {row.slice(0, 6).map((cell, cellIndex) => (
                      <td key={cellIndex}
                        style={{
                          fontSize: '14px',
                          padding: '12px 16px',
                          color: cellIndex === headers.length - 1 ? 'inherit' : '#212529',
                          verticalAlign: 'middle',
                          whiteSpace: 'nowrap',
                          backgroundColor: 'white',
                          border: '1px solid #E3EBF8',
                          fontWeight: cellIndex === 0 || cellIndex === 1 ? '600' : '400'
                        }}
                        className={cellIndex === 2 || cellIndex === 3 || cellIndex === 4 ? 'secondary-text' : ''}
                      >
                        {cellIndex === 5 && getStatusBadgeClass ? (
                          <span style={getStatusBadgeClass(cell)}>
                            {cell}
                          </span>
                        ) : cell}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={headers.length} className="text-center py-4">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {showPagination && (
          <div className="d-flex justify-content-end align-items-center mt-4">
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn bg-white text-black btn-outline-borders d-flex align-items-center px-2"
                style={{ height: '32px', fontSize: '14px', minWidth: '80px' }}
                onClick={() => onPageChange?.(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
              >
                <FaArrowLeft className="me-1" /> Prev
              </button>

              <div className="d-flex gap-2">
                {getPageNumbers().map((pageNum, index) => (
                  <button
                    key={index}
                    // className={`btn btn-outline-borders ${pageNum === currentPage ? 'active' : ''}`}
                    className={`btn ${pageNum === currentPage ? 'bg-black text-white' : 'btn-outline-borders bg-white text-black'}`}

                    style={{
                      height: '32px',
                      minWidth: '32px',
                      fontSize: '14px',
                      borderRadius: '4px',
                      padding: '0 12px',
                      fontWeight: pageNum === currentPage ? 'bold' : 'normal',
                      backgroundColor: pageNum === currentPage ? '#E3EBF8' : '#fff',
                      cursor: pageNum === '...' ? 'default' : 'pointer',
                      pointerEvents: pageNum === '...' ? 'none' : 'auto'
                    }}
                    onClick={() => pageNum !== '...' && onPageChange?.(pageNum)}
                    disabled={pageNum === '...'}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                className="btn bg-white text-black btn-outline-borders d-flex align-items-center px-2"
                style={{ height: '32px', fontSize: '14px', minWidth: '80px' }}
                onClick={() => onPageChange?.(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <FaArrowRight className="ms-1" />
              </button>

              <select
                className="form-select ms-2 bg-white text-black btn-outline-borders"
                style={{
                  width: '145px',
                  height: '34px',
                  fontSize: '14px',
                  borderRadius: '6px',
                }}
                value={pageSize}
                onChange={e => {
                  onPageSizeChange?.(Number(e.target.value));
                  onPageChange?.(1);
                }}
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>
        )}

        {/* Add the confirmation modal */}
        {showDeleteConfirm && (
          <ConfirmationModal
            show={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            icon={DeleteIcon}
            title="Delete Appointment"
            message="Are you sure want to delete this Appointment?"
            primaryText="Delete"
            secondaryText="Cancel"
            onPrimary={confirmDelete}
            onSecondary={() => setShowDeleteConfirm(false)}
          />
        )}

        {/* Add the appointment sidebar */}
        {selectedAppointment && (
          <>
            <div 
              className="appointment-sidebar-overlay" 
              onClick={() => handleCloseAppointment()}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1040
              }}
            />
            <div 
              className="appointment-sidebar-popup"
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                width: '450px',
                backgroundColor: 'white',
                zIndex: 1050,
                overflowY: 'auto',
                boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.1)',
                padding: '20px'
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">{appointmentMode === 'edit' ? 'Edit Appointment' : 'Appointment Details'}</h5>
                <button
                  className="btn btn-link p-0"
                  onClick={() => handleCloseAppointment()}
                  style={{ color: '#000' }}
                >
                  <FaTimes />
                </button>
              </div>

              <NewAppointment
                initialData={selectedAppointment}
                bookingId={selectedAppointment?.bookingId || selectedAppointment?.details?.booking_id}
                holidayDates={holidayDates}
                doctors={doctors}
                timeSlots={availableTimeSlots}
                mode={appointmentMode}
                onClose={(nextMode) => {
                  handleCloseAppointment(nextMode);
                  if (callApi) callApi(); 
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Table;
